import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import {
  InnerModule,
  InnerModulePermissionRow,
  Module,
  ModulePermissionGroup,
  Permission,
  Role,
  SubModule,
  SubModulePermissionRow
} from '../rbac.models';
import { PermissionService } from '../../../../shared/services/rbackservices/permission.service';
import { RoleService } from '../../../../shared/services/rbackservices/role.service';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { ToastService } from '../../../../shared/services/toast.service';

/**
 * Permission matrix for a specific role identified by its `type` (login-type name)
 * passed as `:loginType` route parameter.
 *
 * - Super-category roles  → all modules shown immediately.
 * - Normal-category roles → normal modules shown first; "Show More" reveals super modules.
 * - Submodules are nested under their parent module.
 * - Inner modules (type 'I') are nested under their parent submodule.
 * - Three checkboxes per row: Read, Add, Edit.
 */
@Component({
  selector: 'app-permission-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './permission-management.component.html',
  styleUrls: ['./permission-management.component.scss']
})
export class PermissionManagementComponent implements OnInit {
  loginType = '';
  loginId = ''
  role: Role | null = null;
  roleCategory: string = 'admin'; // 'super' | 'normal'

  /** All groups (module + nested submodule rows, each submodule row nesting its own inner module rows) */
  allGroups: any[] = [];
  /** Groups currently visible (filtered by category + showMore) */
  visibleGroups: ModulePermissionGroup[] = [];
  /** Hidden groups that appear after "Show More" */
  hiddenGroups: ModulePermissionGroup[] = [];
  private categoryTypeToName: Record<string, string> = {};
  private allSiteCategoryType: string | null = null;

  showMore = false;
  hasHiddenGroups = false;

  private allPermissions: Permission[] = [];

  loading = false;
  saving = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private roleService: RoleService,
    private configurationService: ApiCallsService,
    private permissionService: PermissionService,
    private toast:ToastService
  ) { }

ngOnInit(): void {
  this.loadRoleCategoriesMap();
  this.route.paramMap.subscribe(params => {
    this.loginType = params.get('loginType') ?? '';
    const stored = localStorage.getItem(this.loginType) ?? '';
    this.loginId = this.safeAtob(stored);
    if (this.loginType) {
      this.loadRoleAndMatrix();
    }
  });
}

private safeAtob(value: string): string {
  if (!value) return '';
  try {
    return atob(value);
  } catch {
    // value wasn't base64 (e.g. leftover plain-text entry from before this change)
    return value;
  }
}
private loadRoleCategoriesMap(): void {
  const roleCategories = JSON.parse(localStorage.getItem('role_categories') || '[]');
  this.categoryTypeToName = {};
  this.allSiteCategoryType = null;

  roleCategories.forEach((rc: any) => {
    const typeId = String(rc.category_type);
    const name = String(rc.name || rc._id || '').toLowerCase().trim();
    this.categoryTypeToName[typeId] = name;
    if (name === 'multi site' || name === 'all site') {
      this.allSiteCategoryType = typeId;
    }
  });
}

/** Resolves a category_type id to its display name, e.g. "2" -> "Single Site" */
getCategoryName(categoryType: string | number | undefined | null): string {
  if (categoryType === undefined || categoryType === null || categoryType === '') return 'Admin';
  const name = this.categoryTypeToName[String(categoryType)];
  return name ? name : String(categoryType);
}
private loadRoleAndMatrix(): void {
  this.loading = true;
  this.roleService.get().pipe(
    switchMap((roleRes) => {
      const roles: Role[] = roleRes?.Data ?? roleRes ?? [];
      this.role = roles.find(r => r._id === this.loginId) ?? null;
      console.log(this.role);

      this.roleCategory = this.role?.category ?? 'admin';
      return this.configurationService.get(); // one call, everything
    }),
    switchMap((configRes) => {
      console.log('CONFIG RESPONSE', configRes); // ← temporary
      const all: any[] = configRes?.Data ?? configRes ?? [];
      const modules: Module[] = all.filter(row => row.type === 'M');
      const subModules: SubModule[] = all.filter(row => row.type === 'S');
      const innerModules: InnerModule[] = all.filter(row => row.type === 'I');

      const subModulesByModule: Record<string, SubModule[]> = {};
      modules.forEach(m => {
        subModulesByModule[m._id!] = subModules.filter(sm => sm.parent_id === m._id);
      });

      const innerModulesBySubModule: Record<string, InnerModule[]> = {};
      subModules.forEach(sm => {
        innerModulesBySubModule[sm._id!] = innerModules.filter(im => im.parent_id === sm._id);
      });

      return of({ modules, subModulesByModule, innerModulesBySubModule });
    }),
    switchMap(({ modules, subModulesByModule, innerModulesBySubModule }) =>
      this.permissionService.get().pipe(
        catchError(() => of({ Data: [] })),
        switchMap(permRes => {
          this.allPermissions = (permRes?.Data ?? permRes ?? []).map((p: any) => ({
            ...p,
            id: p._id,
            _id: undefined
          }));
          return of({ modules, subModulesByModule, innerModulesBySubModule });
        })
      )
    ),
    finalize(() => (this.loading = false))
  ).subscribe(({ modules, subModulesByModule, innerModulesBySubModule }) => {
    this.allGroups = modules.map(module => ({
      module,
      modulePermission: this.findModulePermission(module._id!),
      rows: (subModulesByModule[module._id!] || []).map(subModule => ({
        subModule,
        permission: this.findPermission(subModule._id!),
        innerModuleRows: (innerModulesBySubModule[subModule._id!] || []).map(innerModule => ({
          innerModule,
          permission: this.findPermission(innerModule._id!)
        }))
      }))
    }));

    this.allGroups.forEach(group => {
      group.rows.forEach((row: SubModulePermissionRow) => {
        if (row.innerModuleRows.length) {
          this.syncSubmoduleViewFromInnerModules(row);
        }
      });
      if (group.rows.length) {
        this.syncModuleViewFromSubmodules(group);
      }
    });

    this.buildVisibleGroups();
  });
}
private findModulePermission(moduleId: string): Permission | null {
  return this.allPermissions.find(p => p.role_id === this.loginId && p.config_id === moduleId) ?? null;
}
  isModuleChecked(group: ModulePermissionGroup, key: 'is_view' | 'is_add' | 'is_edit'): boolean {
    return group.modulePermission?.[key] === 'Y';
  }

toggleModule(group: ModulePermissionGroup, key: 'is_view' | 'is_add' | 'is_edit'): void {
  if (!group.modulePermission) {
    group.modulePermission = {
      role_id: this.loginId,
      status: 'Y',
      config_id: group.module._id!,
      is_view: 'N',
      is_add: 'N',
      is_edit: 'N'
    };
  }
  group.modulePermission[key] = group.modulePermission[key] === 'Y' ? 'N' : 'Y';
}
private buildVisibleGroups(): void {
  const roleCat = String(this.roleCategory ?? '').trim();
  console.log(roleCat);

  // "All Site" roles see every module/category immediately, nothing hidden.
  if (this.allSiteCategoryType && roleCat === this.allSiteCategoryType) {
    this.visibleGroups = this.allGroups;
    this.hiddenGroups = [];
    this.hasHiddenGroups = false;
    return;
  }

  // Otherwise: show modules matching the role's own category_type first;
  // everything else is hidden behind "Show More".
  const ownCategoryGroups = this.allGroups.filter(
    g => String(g.module.category ?? '') === roleCat
  );
  const otherGroups = this.allGroups.filter(
    g => String(g.module.category ?? '') !== roleCat
  );

  this.hiddenGroups = otherGroups;
  this.hasHiddenGroups = otherGroups.length > 0;
  this.visibleGroups = this.showMore ? [...ownCategoryGroups, ...otherGroups] : ownCategoryGroups;
}
/**
 * Deterministically maps an arbitrary category string to one of a fixed
 * palette of badge color classes (perm-category--c0 .. c5), so any number
 * of categories gets a consistent, distinguishable color without needing
 * a new SCSS rule per category name.
 */
private static readonly CATEGORY_PALETTE_SIZE = 6;

getCategoryClass(category: string | undefined | null): string {
  const cat = String(category ?? '').toLowerCase();
  let hash = 0;
  for (let i = 0; i < cat.length; i++) {
    hash = (hash * 31 + cat.charCodeAt(i)) >>> 0;
  }
  const idx = hash % PermissionManagementComponent.CATEGORY_PALETTE_SIZE;
  return `perm-category--c${idx}`;
}

  toggleShowMore(): void {
    this.showMore = !this.showMore;
    this.buildVisibleGroups();
  }
private findPermission(configId: string): Permission | null {
  return this.allPermissions.find(p => p.role_id === this.loginId && p.config_id === configId) ?? null;
}

  isChecked(row: SubModulePermissionRow, key: 'is_view' | 'is_add' | 'is_edit'): boolean {
    return row.permission?.[key] === 'Y';
  }

toggle(
  group: ModulePermissionGroup,
  row: SubModulePermissionRow,
  key: 'is_view' | 'is_add' | 'is_edit'
): void {
  if (!row.permission) {
    row.permission = {
      role_id: this.loginId,
      status: 'Y',
      config_id: row.subModule._id!,
      is_view: 'N',
      is_add: 'N',
      is_edit: 'N'
    };
  }
  row.permission[key] = row.permission[key] === 'Y' ? 'N' : 'Y';
  this.syncModuleViewFromSubmodules(group);
}

// ---- Inner module (type 'I') level, nested under a submodule row ----
isInnerModuleChecked(innerModuleRow: InnerModulePermissionRow, key: 'is_view' | 'is_add' | 'is_edit'): boolean {
  return innerModuleRow.permission?.[key] === 'Y';
}

toggleInnerModule(
  group: ModulePermissionGroup,
  row: SubModulePermissionRow,
  innerModuleRow: InnerModulePermissionRow,
  key: 'is_view' | 'is_add' | 'is_edit'
): void {
  if (!innerModuleRow.permission) {
    innerModuleRow.permission = {
      role_id: this.loginId,
      status: 'Y',
      config_id: innerModuleRow.innerModule._id!,
      is_view: 'N',
      is_add: 'N',
      is_edit: 'N'
    };
  }
  innerModuleRow.permission[key] = innerModuleRow.permission[key] === 'Y' ? 'N' : 'Y';

  // Bubble the change up: inner modules -> submodule -> module
  this.syncSubmoduleViewFromInnerModules(row);
  this.syncModuleViewFromSubmodules(group);
}

private syncSubmoduleViewFromInnerModules(row: SubModulePermissionRow): void {
  if (!row.innerModuleRows?.length) return;

  const anyViewable = row.innerModuleRows.some(im => im.permission?.is_view === 'Y');
  if (!row.permission) {
    row.permission = {
      role_id: this.loginId,
      status: 'Y',
      config_id: row.subModule._id!,
      is_view: 'N',
      is_add: 'N',
      is_edit: 'N'
    };
  }
  row.permission.is_view = anyViewable ? 'Y' : 'N';
}

private syncModuleViewFromSubmodules(group: ModulePermissionGroup): void {
  const anyViewable = group.rows.some((r: any) => r.permission?.is_view === 'Y');
  if (!group.modulePermission) {
    group.modulePermission = {
      role_id: this.loginId,
      status: 'Y',
      config_id: group.module._id!,
      is_view: 'N',
      is_add: 'N',
      is_edit: 'N'
    };
  }
  group.modulePermission.is_view = anyViewable ? 'Y' : 'N';
}
save(): void {
    if (!this.loginType) { return; }

    const permissions: Permission[] = [];
    for (const group of this.allGroups) {
      if (group.modulePermission) {
        permissions.push(group.modulePermission);
      }
      for (const row of group.rows) {
        if (row.permission) {
          permissions.push(row.permission);
        }
        for (const innerModuleRow of row.innerModuleRows || []) {
          if (innerModuleRow.permission) {
            permissions.push(innerModuleRow.permission);
          }
        }
      }
    }

    if (!permissions.length) { return; }

    // Permissions that already exist (carry an id from this.allPermissions) go via PUT/update.
    // Brand new ones (toggled on for the first time, no id yet) go via POST/add.
    const existing: Permission[] = permissions.filter(p => !!(p as any).id);
    const fresh: Permission[] = permissions.filter(p => !(p as any).id);
    console.log(existing)
    const calls: any[] = [];
    if (existing.length) {
      calls.push(this.permissionService.update({ permission: existing }));
    }
    if (fresh.length) {
      calls.push(this.permissionService.add({ permission: fresh }));
    }

    if (!calls.length) { return; }

    this.saving = true;
    forkJoin(calls)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (results: any[]) => {
          const errItem = results.find((res: any) => res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error');
          if (errItem) {
            const errorMsg = errItem?.Message || errItem?.message || 'Failed to update Permissions';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
            return;
          }
          this.toast.showToast('Permissions Updated Successfully', 'colored', 5000, 'success');
          this.loadRoleAndMatrix();
        },
        error: err => {
          const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to update Permissions';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
        }
      });
  }

  goBack(): void {
    this.router.navigate(['administrator'], { queryParamsHandling: 'preserve' });
  }
}